import { getClient } from '../core/global-config.js';
import { checkResponseBodyForInsufficientBalance } from '../core/config.js';

/**
 * 查询参数中的约束条件
 */
export interface TemplateSpecConstraint {
  /**
   * 默认值（任意类型）
   */
  default?: any;
  [key: string]: any;
}

/**
 * 模板输入参数定义
 */
export interface TemplateSpecInput {
  /**
   * 节点字段名称
   */
  field: string;
  /**
   * 节点字段id
   */
  node_id: string;
  /**
   * 节点class_type
   */
  node_type: string;
  /**
   * 作者节点字段自定义标题
   */
  title: string;
  /**
   * 节点字段类型
   */
  type: string;
  /**
   * 节点字段是否必须
   */
  required: boolean;
  /**
   * 约束条件
   */
  constraints?: TemplateSpecConstraint;
}

/**
 * 模板输出定义
 */
export interface TemplateSpecOutput {
  /**
   * 节点ID
   */
  node_id: string;
  /**
   * 节点类型
   */
  node_type: string;
}

/**
 * 模板详细信息
 */
export interface TemplateSpec {
  /**
   * 快应用ID
   */
  template_id: string;
  /**
   * 线上快应用名称
   */
  template_name: string;
  /**
   * 线上快应用描述
   */
  description: string;
  /**
   * 线上快应用版本
   */
  version: string;
  /**
   * 输入参数列表
   */
  inputs: TemplateSpecInput[];
  /**
   * 输出节点列表
   */
  outputs?: TemplateSpecOutput[];
}

/**
 * 快应用搜索请求参数
 */
export interface AppSearchParams {
  /**
   * 类型，默认值为 comfyui
   * @default "comfyui"
   */
  type?: string;
  /**
   * 快应用模板ID列表
   */
  template_ids: string[];
}

/**
 * 快应用搜索响应结果
 */
export interface AppSearchResponse {
  /**
   * 类型
   */
  type: string;
  /**
   * 模板信息列表
   */
  templates: TemplateSpec[];
}

/**
 * appSearch
 *
 * 快应用通过template_id查询参数
 *
 * 根据提供的模板ID列表，批量查询快应用的参数配置信息。
 * 返回每个模板的详细规格，包括输入参数定义、约束条件等。
 *
 * @example
 * ```typescript
 * import { initSeacloud, appSearch } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await appSearch({
 *   type: 'comfyui',
 *   template_ids: ['template-id-1', 'template-id-2']
 * });
 *
 * console.log(result.templates);
 * ```
 *
 * @param params 请求参数
 * @returns 模板规格信息
 */
export async function appSearch(
  params: AppSearchParams,
): Promise<AppSearchResponse> {
  const client = await getClient();
  const config = client.getConfig();

  const url = `${config.baseUrl}/model/v1/template/specs`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.timeout);

  try {
    const requestId = `req-${Date.now()}-${Math.random().toString(36).substring(7)}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${config.apiKey}`,
      'X-Request-Id': requestId,
    };

    // 仅在配置了 xProject 时添加 X-Project header
    if (config.xProject) {
      headers['X-Project'] = config.xProject;
    }

    const response = await config.fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorBody}`);
    }

    const result = await response.json();
    checkResponseBodyForInsufficientBalance(result);
    return result as AppSearchResponse;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
}

// 保留旧的函数名作为别名，向后兼容
export const templateSpecs = appSearch;
export type TemplateSpecsParams = AppSearchParams;
export type TemplateSpecsResponse = AppSearchResponse;
